import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  CopyableText,
  DateDisplay,
  Modal,
  Pill,
  SelectMenuDropdown,
  Table,
  ToolTip,
  VendorLogo,
} from "@truevault/aquilo";
import { ActionStyle, TableStyle } from "@truevault/aquilo/src/components/table/Table";
import {
  ArrowUturnLeftIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  NoSymbolIcon,
  TrashIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/solid";
import {
  TrueScanResultsDto,
  TrueScanTrackerAcknowledgement,
  TrueScanTrackerDto,
} from "../../../../common/service/server/dto/TrueScanResultsDto";
import { DateDisplayVariant } from "@truevault/aquilo/src/components/dateDisplay/DateDisplay";
import clsx from "clsx";
import { CookieCategory } from "../../../../common/service/server/dto/CookieScanDto";
import { useOrganizationVendors, usePrimaryOrganizationId } from "../../../hooks";
import { OrganizationDataRecipientDto } from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { isShopify } from "../../../util/vendors";
import { UUIDString } from "../../../../common/service/server/Types";

type GroupedTracker = {
  id: string;
  serviceName: string;
  serviceIconUrl?: string;
  trackers: TrueScanTrackerDto[];
  isGroup: true;
  isExpanded: boolean;
  firstSeenAt?: string;
  // Fields for actions - will be applied to all trackers in group
  type?: string;
  url?: string;
};

type TrackerWithGroupInfo = TrueScanTrackerDto & {
  _groupInfo?: GroupedTracker;
  _isFirstInExpandedGroup?: boolean;
  _isInExpandedGroup?: boolean;
};

type TableRowData = TrueScanTrackerDto | GroupedTracker | TrackerWithGroupInfo;

const isGroupedTracker = (item: TableRowData): item is GroupedTracker => {
  return (item as GroupedTracker).isGroup === true;
};

const isFirstInExpandedGroup = (item: TableRowData): item is TrackerWithGroupInfo => {
  return (item as TrackerWithGroupInfo)._isFirstInExpandedGroup === true;
};

const isUnknownService = (item: any) => {
  return item.serviceName === "Unknown service";
};

const isConsentCheckableCategory = (category: CookieCategory | null) => {
  return category && category !== "FUNCTIONALITY" && category !== "SECURITY";
};

const isOptOutCheckableCategory = (category: CookieCategory | null) => {
  return category === "DATA_SELLING_SHARING";
};

const CONSENT_CATEGORY_OPTIONS: { value: CookieCategory; label: string }[] = [
  { value: "FUNCTIONALITY", label: "Functionality" },
  { value: "ANALYTICS", label: "Analytics" },
  { value: "ADVERTISING", label: "Advertising" },
  { value: "PERSONALIZATION", label: "Personalization" },
  { value: "SECURITY", label: "Security" },
  { value: "DATA_SELLING_SHARING", label: "Data Selling/Sharing" },
];

/** Value type for consent category dropdown - includes special options beyond CookieCategory. */
type ConsentDropdownValue = CookieCategory | "MANAGED_BY_VENDOR" | "IGNORE";

const isGoogleTracker = (item: TrueScanTrackerDto) => {
  return item.serviceName?.toLowerCase().includes("google") === true;
};

const buildUrlShortText = (url: string): string => {
  const hostname = new URL(url).hostname;
  const hostnameEnd = url.indexOf(hostname) + hostname.length;

  let secondPartStart = url.lastIndexOf("/") + 1;
  let secondPart = url.substring(secondPartStart);

  // If second part is too short, expand backwards to get at least 5 chars (might include directory structure).
  if (secondPart.length < 5) {
    secondPartStart = Math.max(hostnameEnd + 1, url.length - 5);
    secondPart = url.substring(secondPartStart);
  }

  // Check for overlap and just use the entire URL if so.
  if (secondPartStart <= hostnameEnd) {
    return url;
  }

  return `https://${hostname}...${secondPart}`;
};

const sortTrackersByServiceName = (a: any, b: any) => {
  const isUnknownA = isUnknownService(a) ? 1 : 0;
  const isUnknownB = isUnknownService(b) ? 1 : 0;

  if (isUnknownA !== isUnknownB) {
    return isUnknownA - isUnknownB;
  }

  if (isUnknownA === 1 && isUnknownB === 1) {
    return a.url.localeCompare(b.url);
  }

  const serviceNameComparison = a.serviceName.localeCompare(b.serviceName);
  return serviceNameComparison !== 0 ? serviceNameComparison : a.url.localeCompare(b.url);
};

const getTooltipForConsentRespecting = (
  item: TrueScanTrackerDto,
  itemIdsWithDirtyCategories?: string[],
  managedByVendor?: OrganizationDataRecipientDto,
): string | null => {
  if (managedByVendor) {
    return `Consent signals that ${managedByVendor.name} receives should control whether this script loads.`;
  }

  if (!item.consentCategories || item.consentCategories?.length === 0) {
    return "Set intended consent categories for this script to enable consent checks.";
  }

  if (item.consentCategories.every((c) => !isConsentCheckableCategory(c))) {
    return "Scripts marked as only Functional or Security do not require consent.";
  }

  if (!itemIdsWithDirtyCategories || !itemIdsWithDirtyCategories.includes(item.id)) {
    // These trackers have not had their categories changed since the last scan.
    if (item.respectingConsents === true) {
      return null;
    } else if (item.respectingConsents === false) {
      if (isGoogleTracker(item)) {
        return `While this script is still loading, it may be sending a limited-data-use flag to the vendor. Many of Google's GTM tags are set up by default to still load in the browser while sending a user's consent preference to the platform. If desired, you can block the tag entirely by specifying 'Additional Consents Required' in the GTM tag's settings.`;
      }
      return "This script still loaded when at least one of the indicated consent requirements was not present.";
    }
  }

  return "Perform another scan to test the consent for this script.";
};

const TrackerRowServiceName = ({ item }: { item: TrueScanTrackerDto }) => (
  <div className="flex" style={{ lineHeight: "18px" }}>
    <div className="w-1/2 pr-2">
      <VendorLogo vendorLogoUrl={item.serviceIconUrl} vendorName={item.serviceName} size="s" />
    </div>
    <div className="w-1/2">
      <span className={clsx(!isUnknownService(item) && "font-semibold text-gray-900")}>
        {item.serviceName}
      </span>
      <br />
      <small className="font-medium text-gray-500 text-transform-capitalize">
        {item.type?.toLowerCase()}
      </small>
    </div>
  </div>
);

const getManagedByVendor = (
  item: TrueScanTrackerDto,
  orgDataRecipients: OrganizationDataRecipientDto[],
): OrganizationDataRecipientDto => {
  return orgDataRecipients.find((dr) => dr.vendorId === item.managedByVendorId);
};

export const TrueScanResults = ({
  lastScan,
  trackersDetected,
  trackersGoneMissing,
  trackersIgnored,
  onUpdateTrackerConsentCategories,
  onUpdateTrackerAcknowledgement,
  onUpdateTrackerManagedByVendor,
  onBulkUpdateTrackers,
  onDownloadCsv,
  className,
  consentScannedFromLocationName,
  optOutScanned,
}: TrueScanResultsDto & {
  onUpdateTrackerConsentCategories?: (
    trackerId: string,
    categories: CookieCategory[],
  ) => Promise<{ success: boolean }>;
  onUpdateTrackerAcknowledgement?: (
    trackerId: string,
    acknowledgement: TrueScanTrackerAcknowledgement,
  ) => Promise<{ success: boolean }>;
  onUpdateTrackerManagedByVendor?: (
    trackerId: string,
    vendorId: UUIDString | null,
  ) => Promise<{ success: boolean }>;
  onBulkUpdateTrackers?: (
    updates: Array<{
      trackerId: string;
      consentCategories?: CookieCategory[];
      acknowledgement?: TrueScanTrackerAcknowledgement;
      managedByVendor?: UUIDString | null;
    }>,
  ) => Promise<{ success: boolean }>;
  onDownloadCsv?: () => Promise<{ success: boolean }>;
  className?: string;
}) => {
  const [selectedValues, setSelectedValues] = useState<{ [key: string]: string[] }>({});
  const [pendingRowIds, setPendingRowIds] = useState<string[]>([]);
  const [dirtyRowIds, setDirtyRowIds] = useState<string[]>([]); // Track rows with changed categories
  const orgId = usePrimaryOrganizationId();
  const [orgDataRecipients] = useOrganizationVendors(orgId);
  const [vendorDialogOpenForTracker, setVendorDialogOpenForTracker] =
    useState<TrueScanTrackerDto>(null);
  const [vendorDialogGroup, setVendorDialogGroup] = useState<GroupedTracker | null>(null);
  const [selectedManagingVendorDataRecipient, setSelectedManagingVendorDataRecipient] =
    useState<OrganizationDataRecipientDto | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [mixedConsentModalOpen, setMixedConsentModalOpen] = useState<{
    group: GroupedTracker;
    action: string;
    value: any;
  } | null>(null);

  const [
    orgDataRecipientsForQuickSelectionAsManagers,
    setOrgDataRecipientsForQuickSelectionAsManagers,
  ] = useState<OrganizationDataRecipientDto[]>(
    orgDataRecipients?.filter((dr) => isShopify(dr)) ?? [],
  );

  const groupTrackersByService = (trackers: TrueScanTrackerDto[]): TableRowData[] => {
    if (!trackers) return [];

    const serviceGroups = new Map<string, TrueScanTrackerDto[]>();

    // Group trackers by service name
    trackers.forEach((tracker) => {
      const serviceName = tracker.serviceName || "Unknown service";
      if (!serviceGroups.has(serviceName)) {
        serviceGroups.set(serviceName, []);
      }
      serviceGroups.get(serviceName)!.push(tracker);
    });

    const result: TableRowData[] = [];

    serviceGroups.forEach((trackersInGroup, serviceName) => {
      if (trackersInGroup.length === 1) {
        // Single tracker, add as-is
        result.push(trackersInGroup[0]);
      } else {
        // Multiple trackers, create group
        // Sort trackers within group first to determine the real first one
        const sortedTrackersInGroup = [...trackersInGroup].sort((a, b) => {
          const isUnknownA = isUnknownService(a) ? 1 : 0;
          const isUnknownB = isUnknownService(b) ? 1 : 0;

          if (isUnknownA !== isUnknownB) {
            return isUnknownA - isUnknownB;
          }

          if (isUnknownA === 1 && isUnknownB === 1) {
            return (a.url || "").localeCompare(b.url || "");
          }

          const serviceNameComparison = a.serviceName.localeCompare(b.serviceName);
          if (serviceNameComparison !== 0) return serviceNameComparison;

          return (a.url || "").localeCompare(b.url || "");
        });

        const primaryTracker = sortedTrackersInGroup[0];
        const groupId = `group-${serviceName}`;
        const isExpanded = expandedGroups.has(groupId);

        const group: GroupedTracker = {
          id: groupId,
          serviceName,
          serviceIconUrl: primaryTracker.serviceIconUrl,
          trackers: sortedTrackersInGroup,
          isGroup: true,
          isExpanded,
          firstSeenAt: sortedTrackersInGroup.reduce((earliest, tracker) => {
            if (!earliest) return tracker.firstSeenAt;
            if (!tracker.firstSeenAt) return earliest;
            return tracker.firstSeenAt < earliest ? tracker.firstSeenAt : earliest;
          }, undefined as string | undefined),
          type: primaryTracker.type,
          url: primaryTracker.url,
        };

        if (isExpanded) {
          // When expanded, show individual trackers with group info on first one
          sortedTrackersInGroup.forEach((tracker, index) => {
            result.push({
              ...tracker,
              _groupInfo: index === 0 ? group : undefined,
              _isFirstInExpandedGroup: index === 0,
              _isInExpandedGroup: true,
            } as any);
          });
        } else {
          // When collapsed, show group row
          result.push(group);
        }
      }
    });

    // Sort by service groups, but preserve order within expanded groups
    return result.sort((a, b) => {
      // First check if these are part of the same expanded group
      const aIsInExpandedGroup = (a as any)._groupInfo || (a as any)._isFirstInExpandedGroup;
      const bIsInExpandedGroup = (b as any)._groupInfo || (b as any)._isFirstInExpandedGroup;

      if (aIsInExpandedGroup && bIsInExpandedGroup) {
        const aGroupId = (a as any)._groupInfo?.id;
        const bGroupId = (b as any)._groupInfo?.id;

        // If they're in the same group, preserve original order (don't sort)
        if (aGroupId === bGroupId || a.serviceName === b.serviceName) {
          return 0; // Preserve original order within the same expanded group
        }
      }

      // Otherwise, apply normal sorting
      const isUnknownA = isUnknownService(a) ? 1 : 0;
      const isUnknownB = isUnknownService(b) ? 1 : 0;

      if (isUnknownA !== isUnknownB) {
        return isUnknownA - isUnknownB;
      }

      if (isUnknownA === 1 && isUnknownB === 1) {
        const urlA = isGroupedTracker(a) ? a.url || "" : a.url || "";
        const urlB = isGroupedTracker(b) ? b.url || "" : b.url || "";
        return urlA.localeCompare(urlB);
      }

      const serviceNameComparison = a.serviceName.localeCompare(b.serviceName);
      if (serviceNameComparison !== 0) return serviceNameComparison;

      const urlA = isGroupedTracker(a) ? a.url || "" : a.url || "";
      const urlB = isGroupedTracker(b) ? b.url || "" : b.url || "";
      return urlA.localeCompare(urlB);
    });
  };

  const toggleGroupExpansion = (groupId: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  // Initialize selectedValues from server data when component mounts or data changes
  useEffect(() => {
    const initialSelectedValues: { [key: string]: string[] } = {};

    // Add categories from categorized trackers
    trackersDetected?.forEach((tracker) => {
      if (tracker.consentCategories && tracker.consentCategories.length > 0) {
        initialSelectedValues[tracker.id] = [...tracker.consentCategories];
      }
    });

    setSelectedValues(initialSelectedValues);
  }, [trackersDetected]);

  const handleCategorizeConsentSelect = async (itemKey: string, newValue: string) => {
    // Update local state first for immediate UI feedback
    const updatedValues = {
      ...selectedValues,
      [itemKey]: selectedValues[itemKey]?.includes(newValue)
        ? selectedValues[itemKey].filter((value) => value !== newValue)
        : [...(selectedValues[itemKey] || []), newValue],
    };
    setSelectedValues(updatedValues);

    // Mark this row as dirty (categories changed)
    setDirtyRowIds((prev) => (prev.includes(itemKey) ? prev : [...prev, itemKey]));

    // Update consent categories on the server
    if (onUpdateTrackerConsentCategories) {
      setPendingRowIds((prev) => [...prev, itemKey]);
      const categories = updatedValues[itemKey] as CookieCategory[];
      try {
        await onUpdateTrackerConsentCategories(itemKey, categories);
      } finally {
        setPendingRowIds((prev) => prev.filter((id) => id !== itemKey));
      }
    }
  };

  const handleAcknowledgeTracker = async (
    trackerId: string,
    acknowledgement: TrueScanTrackerAcknowledgement,
  ) => {
    if (!onUpdateTrackerAcknowledgement) return;

    try {
      setPendingRowIds((prev) => [...prev, trackerId]);
      const result = await onUpdateTrackerAcknowledgement(trackerId, acknowledgement);
      if (result.success) {
        // success
      } else {
        // fail
      }
    } catch (error) {
      // error
    } finally {
      setPendingRowIds((prev) => prev.filter((id) => id !== trackerId));
    }
  };

  const openManagedByDialog = (tracker: TrueScanTrackerDto, group?: GroupedTracker) => {
    setVendorDialogOpenForTracker(tracker);
    setVendorDialogGroup(group || null);
    setSelectedManagingVendorDataRecipient(getManagedByVendor(tracker, orgDataRecipients));
  };

  const closeManagedByDialog = () => {
    setVendorDialogOpenForTracker(null);
    setVendorDialogGroup(null);
    setSelectedManagingVendorDataRecipient(null);
  };

  const handleManagingVendorSelect = async (
    tracker: TrueScanTrackerDto,
    dataRecipient: OrganizationDataRecipientDto | null,
  ) => {
    setSelectedManagingVendorDataRecipient(dataRecipient);

    if (vendorDialogGroup) {
      // Apply to all trackers in the group
      await handleGroupAction(vendorDialogGroup, "vendor", { vendorId: dataRecipient?.vendorId });
    } else {
      // Apply to single tracker
      await onUpdateTrackerManagedByVendor(tracker.id, dataRecipient?.vendorId);
    }

    setOrgDataRecipientsForQuickSelectionAsManagers((prev) => {
      const alreadyIncluded = prev.some((dr) => dr.vendorId === dataRecipient?.vendorId);
      if (dataRecipient && !alreadyIncluded) {
        return [...prev, dataRecipient];
      } else {
        return prev;
      }
    });

    closeManagedByDialog();
  };

  const handleGroupAction = async (
    group: GroupedTracker,
    action: "categorize" | "acknowledge" | "vendor",
    value?: any,
  ) => {
    // onBulkUpdateTrackers is always provided, so no fallback needed
    if (!onBulkUpdateTrackers) {
      console.error("onBulkUpdateTrackers is required for group actions");
      return;
    }

    const updates = group.trackers.map((tracker) => {
      const update: any = { trackerId: tracker.id };

      switch (action) {
        case "categorize": {
          // For collapsed groups, ensure all trackers get consistent categories
          const groupCategories = getGroupConsentCategories(group);
          const isMixedGroup = hasGroupMixedConsentCategories(group);

          let updatedGroupCategories: string[];

          if (isMixedGroup) {
            // For mixed groups, the user is setting a definitive state
            // Since the dropdown shows no categories selected, clicking any category
            // should set ALL trackers to have only that category selected (thus, the group will no longer be mixed).
            updatedGroupCategories = [value];
          } else {
            // For non-mixed groups, use normal toggle behavior
            updatedGroupCategories = groupCategories.includes(value)
              ? groupCategories.filter((cat) => cat !== value) // Remove if present
              : [...groupCategories, value]; // Add if not present
          }

          // Set all trackers to have the same updated categories
          update.consentCategories = updatedGroupCategories;
          break;
        }
        case "acknowledge":
          update.acknowledgement = value;
          break;
        case "vendor":
          update.managedByVendor = value?.vendorId;
          break;
      }

      return update;
    });

    // Update local state for categorize actions
    if (action === "categorize") {
      const newSelectedValues = { ...selectedValues };

      // Get the current group categories (common to all trackers)
      const groupCategories = getGroupConsentCategories(group);
      const isMixedGroup = hasGroupMixedConsentCategories(group);

      let updatedGroupCategories: string[];

      if (isMixedGroup) {
        // For mixed groups, the user is setting a definitive state
        // Since the dropdown shows no categories selected, clicking any category
        // should set ALL trackers to have only that category selected
        updatedGroupCategories = [value];
      } else {
        // For non-mixed groups, use normal toggle behavior
        updatedGroupCategories = groupCategories.includes(value)
          ? groupCategories.filter((cat) => cat !== value) // Remove if present
          : [...groupCategories, value]; // Add if not present
      }

      // Apply the same categories to all trackers in the group
      group.trackers.forEach((tracker) => {
        newSelectedValues[tracker.id] = updatedGroupCategories;
      });
      setSelectedValues(newSelectedValues);

      // Mark all trackers as dirty
      setDirtyRowIds((prev) => {
        const newDirtyIds = group.trackers.map((t) => t.id).filter((id) => !prev.includes(id));
        return [...prev, ...newDirtyIds];
      });
    }

    // Make bulk API call
    try {
      setPendingRowIds((prev) => [...prev, ...group.trackers.map((t) => t.id)]);
      await onBulkUpdateTrackers(updates);
    } finally {
      setPendingRowIds((prev) => prev.filter((id) => !group.trackers.some((t) => t.id === id)));
    }
  };

  const getGroupConsentCategories = (group: GroupedTracker): string[] => {
    // Return categories that are common to all trackers in the group
    if (group.trackers.length === 0) return [];

    const firstTrackerCategories = selectedValues[group.trackers[0].id] || [];
    return firstTrackerCategories.filter((category) =>
      group.trackers.every((tracker) => selectedValues[tracker.id]?.includes(category)),
    );
  };

  const hasGroupMixedConsentCategories = (group: GroupedTracker): boolean => {
    if (group.trackers.length <= 1) return false;

    const firstTrackerCategories = new Set(selectedValues[group.trackers[0].id] || []);
    return group.trackers.slice(1).some((tracker) => {
      const trackerCategories = new Set(selectedValues[tracker.id] || []);
      // Check if categories are different (not subset of each other)
      if (firstTrackerCategories.size !== trackerCategories.size) return true;
      for (const category of firstTrackerCategories) {
        if (!trackerCategories.has(category)) return true;
      }
      return false;
    });
  };

  const columnsForCategorizedTrackers = [
    {
      key: "serviceName",
      header: "Service",
      className: "",
      render: (item: TableRowData) => {
        if (isGroupedTracker(item)) {
          const otherCount = item.trackers.length - 1;
          return (
            <div className="flex" style={{ lineHeight: "18px" }}>
              <div className="w-1/2 pr-2">
                <VendorLogo
                  vendorLogoUrl={item.serviceIconUrl}
                  vendorName={item.serviceName}
                  size="s"
                />
              </div>
              <div className="w-1/2">
                <div className="flex items-center gap-1">
                  <span className={clsx(!isUnknownService(item) && "font-semibold text-gray-900")}>
                    {item.serviceName}
                    {item.trackers.some((t) => t.firstSeenAt && !t.lastSeenAt) && (
                      <Pill variant="info">New</Pill>
                    )}
                  </span>
                  <button
                    onClick={() => toggleGroupExpansion(item.id)}
                    className="flex items-center p-0 border-0 bg-transparent cursor-pointer"
                  >
                    {item.isExpanded ? (
                      <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronRightIcon className="w-4 h-4 text-gray-500" />
                    )}
                  </button>
                </div>
                {!item.isExpanded && otherCount > 0 && (
                  <small className="font-medium text-gray-500">
                    {otherCount} additional script{otherCount > 1 ? "s" : ""}
                    {isUnknownService(item) ? "" : ` from ${item.serviceName}`} detected
                  </small>
                )}
              </div>
            </div>
          );
        } else if (isFirstInExpandedGroup(item) && item._groupInfo) {
          // First tracker in expanded group - show collapse button
          const groupInfo = item._groupInfo;
          return (
            <div className="flex" style={{ lineHeight: "18px" }}>
              <div className="w-1/2 pr-2">
                <VendorLogo
                  vendorLogoUrl={item.serviceIconUrl}
                  vendorName={item.serviceName}
                  size="s"
                />
              </div>
              <div className="w-1/2">
                <div className="flex items-center gap-1">
                  <span className={clsx(!isUnknownService(item) && "font-semibold text-gray-900")}>
                    {item.serviceName}
                    {item.firstSeenAt && !item.lastSeenAt && <Pill variant="info">New</Pill>}
                  </span>
                  <button
                    onClick={() => toggleGroupExpansion(groupInfo.id)}
                    className="flex items-center p-0 border-0 bg-transparent cursor-pointer"
                  >
                    <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
                <small className="font-medium text-gray-500 text-transform-capitalize">
                  {item.type?.toLowerCase()}
                </small>
              </div>
            </div>
          );
        } else if ((item as TrackerWithGroupInfo)._isInExpandedGroup) {
          // Subsequent trackers in expanded group - hide service name, indent
          return (
            <div className="flex pl-[38px]" style={{ lineHeight: "18px" }}>
              <div className="w-1/2 flex items-center gap-1">
                <small className="font-medium text-gray-500 text-transform-capitalize pl-[8px]">
                  {item.type?.toLowerCase()}
                </small>
                {item.firstSeenAt && !item.lastSeenAt && <Pill variant="info">New</Pill>}
              </div>
            </div>
          );
        } else {
          return (
            <div className="flex" style={{ lineHeight: "18px" }}>
              <div className="w-1/2 pr-2">
                <VendorLogo
                  vendorLogoUrl={item.serviceIconUrl}
                  vendorName={item.serviceName}
                  size="s"
                />
              </div>
              <div className="w-1/2">
                <span className={clsx(!isUnknownService(item) && "font-semibold text-gray-900")}>
                  {item.serviceName}
                  {item.firstSeenAt && !item.lastSeenAt && <Pill variant="info">New</Pill>}
                </span>
                <br />
                <small className="font-medium text-gray-500 text-transform-capitalize">
                  {item.type?.toLowerCase()}
                </small>
              </div>
            </div>
          );
        }
      },
    },
    {
      key: "url",
      header: "URLs",
      render: (item: TableRowData) => {
        if (isGroupedTracker(item)) {
          const primaryUrl = item.trackers[0]?.url;
          const otherUrls = item.trackers.length - 1;

          return (
            <div className="flex align-items-baseline">
              {primaryUrl && (
                <CopyableText
                  className="text-sm"
                  style={{ left: "-0.7rem" }}
                  maxWidth={"400px"}
                  shortText={buildUrlShortText(primaryUrl)}
                  longText={primaryUrl}
                />
              )}
              {!item.isExpanded && otherUrls > 0 && (
                <div>
                  <small className="text-gray-500">+{otherUrls} more</small>
                </div>
              )}
            </div>
          );
        } else {
          return (
            <>
              <CopyableText
                className="text-sm"
                style={{ left: "-0.7rem" }}
                maxWidth={"400px"}
                shortText={buildUrlShortText(item.url)}
                longText={item.url}
              />
            </>
          );
        }
      },
    },
    {
      key: "consentCategories",
      header: "Consent Categories",
      render: (item: TableRowData) => {
        if (isGroupedTracker(item)) {
          if (!item.isExpanded) {
            // Show group controls only when collapsed
            const groupCategories = getGroupConsentCategories(item);
            const hasAllSameManagedBy = item.trackers.every(
              (t) => t.managedByVendorId === item.trackers[0].managedByVendorId,
            );
            const isManagedByVendor =
              hasAllSameManagedBy && item.trackers[0].managedByVendorId != null;

            const baseCategories = CONSENT_CATEGORY_OPTIONS.map((c) => ({
              value: c.value as ConsentDropdownValue,
              label: c.label,
              isSelected: hasGroupMixedConsentCategories(item)
                ? false
                : groupCategories.includes(c.value),
              isRecommended: item.trackers.some((t) =>
                t.consentCategoriesRecommended?.includes(c.value),
              ),
              hasDivider: false,
              hideFromList: false,
              isDisabled: false,
              disabledTooltip: null,
            }));

            // Add additional options only for non-unknown services
            const additionalOptions: Array<{
              value: ConsentDropdownValue;
              label: string;
              isSelected: boolean;
              isRecommended: boolean;
              hasDivider: boolean;
              hideFromList: boolean;
              isDisabled: boolean;
              disabledTooltip: string | null;
            }> = !isUnknownService(item)
              ? [
                  {
                    value: "MANAGED_BY_VENDOR",
                    label: "Managed by a platform",
                    isSelected: isManagedByVendor,
                    isRecommended: false,
                    hasDivider: true,
                    hideFromList: true,
                    isDisabled: item.trackers.some((t) => t.managedByVendorIdLocked === true),
                    disabledTooltip: "This script is known to be managed by a platform.",
                  },
                  {
                    value: "IGNORE",
                    label: "Ignore (not a tracker)",
                    isSelected: false,
                    isRecommended: false,
                    hasDivider: false,
                    hideFromList: true,
                    isDisabled: false,
                    disabledTooltip: null,
                  },
                ]
              : [];

            return (
              <SelectMenuDropdown<ConsentDropdownValue>
                items={baseCategories.concat(additionalOptions)}
                onSelect={async (newValue) => {
                  if (newValue === "IGNORE") {
                    await handleGroupAction(item, "acknowledge", "IGNORED");
                    return;
                  }
                  if (newValue === "MANAGED_BY_VENDOR") {
                    if (isManagedByVendor) {
                      await handleGroupAction(item, "vendor", { vendorId: null });
                    } else {
                      openManagedByDialog(item.trackers[0], item);
                    }
                    return;
                  }

                  // Check for mixed consent categories before applying change
                  if (hasGroupMixedConsentCategories(item)) {
                    setMixedConsentModalOpen({
                      group: item,
                      action: "categorize",
                      value: newValue,
                    });
                    return;
                  }

                  await handleGroupAction(item, "categorize", newValue as CookieCategory);
                }}
                allowMultipleSelected
                isVerticallyStacked={true}
                selectedOverflowItemName="categories"
                noSelectedErrorMessage={
                  hasGroupMixedConsentCategories(item) ? "Various consents" : "Select consents"
                }
                noSelectedErrorMessageShowAsRed={!hasGroupMixedConsentCategories(item)}
                dropdownWidthPx={300}
                maxWidth={"200px"}
                fullwidth
              />
            );
          } else {
            // Hide for expanded groups, show on individual trackers
            return null;
          }
        } else {
          const typedItem = item as TrueScanTrackerDto;
          return (
            <SelectMenuDropdown<ConsentDropdownValue>
              items={CONSENT_CATEGORY_OPTIONS.map((c) => ({
                value: c.value as ConsentDropdownValue,
                label: c.label,
                isSelected: selectedValues[typedItem.id]?.includes(c.value),
                isRecommended: typedItem.consentCategoriesRecommended?.includes(c.value),
                hasDivider: false,
                hideFromList: false,
                isDisabled: false,
                disabledTooltip: null,
              })).concat([
                {
                  value: "MANAGED_BY_VENDOR" as ConsentDropdownValue,
                  label: "Managed by a platform",
                  isSelected: typedItem.managedByVendorId != null,
                  isRecommended: false,
                  hasDivider: true,
                  hideFromList: true,
                  isDisabled: typedItem.managedByVendorIdLocked === true,
                  disabledTooltip: "This script is known to be managed by a platform.",
                },
                {
                  value: "IGNORE" as ConsentDropdownValue,
                  label: "Ignore (not a tracker)",
                  isSelected: false,
                  isRecommended: false,
                  hasDivider: false,
                  hideFromList: true,
                  isDisabled: false,
                  disabledTooltip: null,
                },
              ])}
              onSelect={async (newValue) => {
                if (newValue === "IGNORE") {
                  await handleAcknowledgeTracker(typedItem.id, "IGNORED");
                  return;
                }
                if (newValue === "MANAGED_BY_VENDOR") {
                  if (typedItem.managedByVendorId) {
                    await handleManagingVendorSelect(typedItem, null);
                  } else {
                    openManagedByDialog(typedItem);
                  }
                  return;
                }
                await handleCategorizeConsentSelect(typedItem.id, newValue as CookieCategory);
              }}
              allowMultipleSelected
              isVerticallyStacked={true}
              selectedOverflowItemName="categories"
              noSelectedErrorMessage="Select consents"
              dropdownWidthPx={300}
              maxWidth={"200px"}
              fullwidth
            />
          );
        }
      },
    },
    {
      key: "firstSeenAt",
      header: "First Seen",
      render: (item) => <DateDisplay date={item.firstSeenAt} hoverForRelative />,
    },
  ];

  // Only show the "Respecting Consent?" column if org has a consent-controlled banner enabled for EEA or CA.
  if (consentScannedFromLocationName) {
    columnsForCategorizedTrackers.push({
      key: "respectingConsents",
      header: "Respecting Consent?",
      className: "text-center",
      render: (item: TableRowData) => {
        if (isGroupedTracker(item)) {
          if (!item.isExpanded) {
            // Show group status only when collapsed
            // For groups, show aggregated status
            const allRespecting = item.trackers.every((t) => t.respectingConsents === true);
            const noneRespecting = item.trackers.every((t) => t.respectingConsents === false);
            const allManagedBySame = item.trackers.every(
              (t) => t.managedByVendorId === item.trackers[0].managedByVendorId,
            );
            const managedByVendor =
              allManagedBySame && item.trackers[0].managedByVendorId
                ? getManagedByVendor(item.trackers[0] as TrueScanTrackerDto, orgDataRecipients)
                : null;

            // Check for "scan again" vs "n/a" states
            const someNeedScanAgain = item.trackers.some((t) => {
              const trackerCategories = selectedValues[t.id] || [];
              return (
                (t.respectingConsents == null || dirtyRowIds?.includes(t.id)) &&
                trackerCategories.some((c) => isConsentCheckableCategory(c as CookieCategory))
              );
            });
            const allNA = item.trackers.every((t) => {
              const trackerCategories = selectedValues[t.id] || [];
              return (
                t.respectingConsents == null &&
                trackerCategories.length > 0 &&
                trackerCategories.every((c) => !isConsentCheckableCategory(c as CookieCategory))
              );
            });
            const noneHaveConsentCategories = item.trackers.every((t) => {
              const trackerCategories = selectedValues[t.id] || [];
              return trackerCategories.length === 0;
            });

            const inside = (
              <div className="flex gap-1 justify-center align-items-center">
                {noneHaveConsentCategories ? (
                  <></>
                ) : managedByVendor ? (
                  <div className="flex flex-col items-center">
                    <small>Managed by</small>
                    <strong className="flex items-center">
                      <VendorLogo
                        vendorLogoUrl={managedByVendor.logoUrl}
                        vendorName={managedByVendor.name}
                        size="xs"
                      />
                      <span className="ml-xxs">{managedByVendor.name}</span>
                    </strong>
                  </div>
                ) : allNA ? (
                  <small className="text-gray-400">N/A</small>
                ) : someNeedScanAgain ? (
                  <small className="text-gray-400">Scan again</small>
                ) : allRespecting ? (
                  <>
                    <CheckIcon className="w-[20px] h-[20px] text-green-600" />
                    <span className="text-green-600">Yes</span>
                  </>
                ) : noneRespecting ? (
                  <>
                    <NoSymbolIcon className="w-[20px] h-[20px] text-red-500" />
                    <span className="text-red-500">No</span>
                  </>
                ) : (
                  <>
                    <NoSymbolIcon className="w-[20px] h-[20px] text-orange-500" />
                    <span className="text-orange-500">Mixed</span>
                  </>
                )}
                {/* Add info icon for states that have tooltips (excluding managed vendors) */}
                {!managedByVendor && !allRespecting && (
                  <InformationCircleIcon className="w-[20px] h-[20px] text-gray-500" />
                )}
              </div>
            );

            // Add tooltips for different states
            let tooltipText = "";

            if (noneHaveConsentCategories) {
              tooltipText =
                "Set intended consent categories for these scripts to enable consent checks.";
            } else if (managedByVendor) {
              tooltipText = `Consent signals that ${managedByVendor.name} receives should control whether these scripts load.`;
            } else if (allNA) {
              tooltipText = "Scripts marked as only Functional or Security do not require consent.";
            } else if (someNeedScanAgain) {
              tooltipText = "Some scripts need to be scanned again. Expand to see details.";
            } else if (noneRespecting) {
              tooltipText =
                "These scripts still loaded when at least one of the indicated consent requirements was not present.";
            } else if (!allRespecting && !noneRespecting) {
              // Mixed state - calculate breakdown
              const respectingCount = item.trackers.filter(
                (t) => t.respectingConsents === true,
              ).length;
              const notRespectingCount = item.trackers.filter(
                (t) => t.respectingConsents === false,
              ).length;
              const scanAgainCount = item.trackers.filter((t) => {
                const trackerCategories = selectedValues[t.id] || [];
                return (
                  (t.respectingConsents == null || dirtyRowIds?.includes(t.id)) &&
                  trackerCategories.some((c) => isConsentCheckableCategory(c as CookieCategory))
                );
              }).length;
              const naCount = item.trackers.filter((t) => {
                const trackerCategories = selectedValues[t.id] || [];
                return (
                  t.respectingConsents == null &&
                  trackerCategories.length > 0 &&
                  trackerCategories.every((c) => !isConsentCheckableCategory(c as CookieCategory))
                );
              }).length;

              const parts: string[] = [];
              if (respectingCount > 0) parts.push(`${respectingCount} respecting`);
              if (notRespectingCount > 0) parts.push(`${notRespectingCount} not respecting`);
              if (scanAgainCount > 0) parts.push(`${scanAgainCount} need scan`);
              if (naCount > 0) parts.push(`${naCount} N/A`);
              tooltipText =
                "Mixed consent status across scripts: " +
                parts.join(", ") +
                ". Expand to see details.";
            }

            // Return with tooltip if there's tooltip text
            if (tooltipText) {
              return (
                <ToolTip text={tooltipText} maxWidth="300px" minWidth="200px">
                  {inside}
                </ToolTip>
              );
            }

            return inside;
          } else {
            // Hide for expanded groups, show on individual trackers
            return null;
          }
        } else {
          const typedItem = item as TrueScanTrackerDto;
          const isLDUStyleTracker = isGoogleTracker(typedItem);
          const noConsentTextClass = isLDUStyleTracker ? "text-gray-500" : "text-red-500";
          const managedByVendor = getManagedByVendor(typedItem, orgDataRecipients);
          const tooltipText = getTooltipForConsentRespecting(
            typedItem,
            dirtyRowIds,
            managedByVendor,
          );
          const isPending = dirtyRowIds?.includes(typedItem.id);

          const inside = (
            <div className="flex gap-1 justify-center align-items-center">
              {managedByVendor ? (
                <div className="flex flex-col items-center">
                  <small>Managed by</small>
                  <strong className="flex items-center">
                    <VendorLogo
                      vendorLogoUrl={managedByVendor.logoUrl}
                      vendorName={managedByVendor.name}
                      size="xs"
                    />
                    <span className="ml-xxs">{managedByVendor.name}</span>
                  </strong>
                </div>
              ) : isPending || typedItem.respectingConsents == null ? (
                <small className="text-gray-400">
                  {typedItem.consentCategories?.some((c) => isConsentCheckableCategory(c))
                    ? "Scan again"
                    : typedItem.consentCategories?.length > 0
                    ? "N/A"
                    : null}
                </small>
              ) : !typedItem.consentCategories ||
                typedItem.consentCategories.length === 0 ? null : typedItem.consentCategories.every(
                  // No consent categorization - show blank/null
                  (c) => !isConsentCheckableCategory(c),
                ) ? (
                // All categories are Functional or Security - show N/A
                <small className="text-gray-400">N/A</small>
              ) : typedItem.respectingConsents ? (
                <>
                  <CheckIcon className="w-[20px] h-[20px] text-green-600" />
                  <span className="text-green-600">Yes</span>
                </>
              ) : typedItem.respectingConsents === false ? (
                <>
                  <NoSymbolIcon className={clsx("w-[20px] h-[20px]", noConsentTextClass)} />
                  <span className={clsx(noConsentTextClass)}>No</span>
                </>
              ) : null}
              {tooltipText && !managedByVendor && (
                <InformationCircleIcon className="w-[20px] h-[20px] text-gray-500" />
              )}
            </div>
          );

          if (tooltipText) {
            return (
              <ToolTip text={tooltipText} maxWidth="400px" minWidth="200px">
                {inside}
              </ToolTip>
            );
          } else {
            return inside;
          }
        }
      },
    });
  }

  // Only show the "Respecting Opt-Out?" column if org has opt-out scan data.
  if (optOutScanned) {
    columnsForCategorizedTrackers.push({
      key: "respectingOptOut",
      header: "Respecting Opt-Out?",
      className: "text-center",
      render: (item: TableRowData) => {
        if (isGroupedTracker(item)) {
          if (!item.isExpanded) {
            const optOutApplicableTrackers = item.trackers.filter((t) =>
              (selectedValues[t.id] || t.consentCategories || []).some((c) =>
                isOptOutCheckableCategory(c as CookieCategory),
              ),
            );

            const allRespecting =
              optOutApplicableTrackers.length > 0 &&
              optOutApplicableTrackers.every((t) => t.respectingOptOut === true);
            const noneRespecting =
              optOutApplicableTrackers.length > 0 &&
              optOutApplicableTrackers.every((t) => t.respectingOptOut === false);
            const allManagedBySame =
              optOutApplicableTrackers.length > 0 &&
              optOutApplicableTrackers.every(
                (t) => t.managedByVendorId === optOutApplicableTrackers[0].managedByVendorId,
              );
            const managedByVendor =
              allManagedBySame && optOutApplicableTrackers[0].managedByVendorId
                ? getManagedByVendor(
                    optOutApplicableTrackers[0] as TrueScanTrackerDto,
                    orgDataRecipients,
                  )
                : null;

            const someNeedScanAgain = optOutApplicableTrackers.some((t) => {
              return t.respectingOptOut == null || dirtyRowIds?.includes(t.id);
            });
            const allNA = item.trackers.every((t) => {
              const trackerCategories = selectedValues[t.id] || [];
              return (
                t.respectingOptOut == null &&
                trackerCategories.length > 0 &&
                !trackerCategories.some((c) => isOptOutCheckableCategory(c as CookieCategory))
              );
            });
            const noneHaveOptOutCategory = item.trackers.every((t) => {
              const trackerCategories = selectedValues[t.id] || [];
              return !trackerCategories.some((c) => isOptOutCheckableCategory(c as CookieCategory));
            });

            const inside = (
              <div className="flex gap-1 justify-center align-items-center">
                {noneHaveOptOutCategory ? (
                  <small className="text-gray-400">N/A</small>
                ) : managedByVendor ? (
                  <div className="flex flex-col items-center">
                    <small>Managed by</small>
                    <strong className="flex items-center">
                      <VendorLogo
                        vendorLogoUrl={managedByVendor.logoUrl}
                        vendorName={managedByVendor.name}
                        size="xs"
                      />
                      <span className="ml-xxs">{managedByVendor.name}</span>
                    </strong>
                  </div>
                ) : allNA ? (
                  <small className="text-gray-400">N/A</small>
                ) : someNeedScanAgain ? (
                  <small className="text-gray-400">Scan again</small>
                ) : allRespecting ? (
                  <>
                    <CheckIcon className="w-[20px] h-[20px] text-green-600" />
                    <span className="text-green-600">Yes</span>
                  </>
                ) : noneRespecting ? (
                  <>
                    <NoSymbolIcon className="w-[20px] h-[20px] text-red-500" />
                    <span className="text-red-500">No</span>
                  </>
                ) : (
                  <>
                    <NoSymbolIcon className="w-[20px] h-[20px] text-orange-500" />
                    <span className="text-orange-500">Mixed</span>
                  </>
                )}
                {!managedByVendor && !allRespecting && (
                  <InformationCircleIcon className="w-[20px] h-[20px] text-gray-500" />
                )}
              </div>
            );

            let tooltipText = "";

            if (noneHaveOptOutCategory) {
              const allFunctionalOrSecurityOnly = item.trackers.every((t) => {
                const cats = (selectedValues[t.id] ??
                  t.consentCategories ??
                  []) as CookieCategory[];
                return (
                  cats.length > 0 && cats.every((c) => c === "FUNCTIONALITY" || c === "SECURITY")
                );
              });
              tooltipText = allFunctionalOrSecurityOnly
                ? "Scripts marked as only Functional or Security do not require opt-out checks."
                : "Scripts not categorized as Data Selling/Sharing do not require opt-out checks.";
            } else if (managedByVendor) {
              tooltipText = `Opt-out signals that ${managedByVendor.name} receives should control whether these scripts load.`;
            } else if (allNA) {
              tooltipText =
                "Scripts marked as only Functional or Security do not require opt-out checks.";
            } else if (someNeedScanAgain) {
              tooltipText = "Some scripts need to be scanned again. Expand to see details.";
            } else if (noneRespecting) {
              tooltipText = "These scripts still loaded when an opt-out or GPC signal was active.";
            } else if (!allRespecting && !noneRespecting) {
              const respectingCount = optOutApplicableTrackers.filter(
                (t) => t.respectingOptOut === true,
              ).length;
              const notRespectingCount = optOutApplicableTrackers.filter(
                (t) => t.respectingOptOut === false,
              ).length;
              tooltipText = `${respectingCount} of ${optOutApplicableTrackers.length} scripts respected opt-out, ${notRespectingCount} did not.`;
            }

            if (tooltipText) {
              return (
                <ToolTip text={tooltipText} maxWidth="300px" minWidth="200px">
                  {inside}
                </ToolTip>
              );
            }

            return inside;
          } else {
            return null;
          }
        } else {
          const typedItem = item as TrueScanTrackerDto;
          const managedByVendor = getManagedByVendor(typedItem, orgDataRecipients);
          const isPending = dirtyRowIds?.includes(typedItem.id);

          const getTooltipForOptOutRespecting = (): string | null => {
            if (managedByVendor) {
              return `Opt-out signals that ${managedByVendor.name} receives should control whether this script loads.`;
            }
            const categories = (selectedValues[typedItem.id] ??
              typedItem.consentCategories ??
              []) as CookieCategory[];
            if (categories.length === 0) {
              return "Scripts not categorized as Data Selling/Sharing do not require opt-out checks.";
            }
            // Functional/Security only: categories are exclusively Functionality and/or Security
            if (categories.every((c) => c === "FUNCTIONALITY" || c === "SECURITY")) {
              return "Scripts marked as only Functional or Security do not require opt-out checks.";
            }
            // Has other categories (Analytics, Advertising, etc.) but none are Data Selling/Sharing
            if (!categories.some((c) => isOptOutCheckableCategory(c))) {
              return "Scripts not categorized as Data Selling/Sharing do not require opt-out checks.";
            }
            if (typedItem.respectingOptOut === false) {
              if (isGoogleTracker(typedItem)) {
                return `While this script is still loading, it may be sending a limited-data-use flag to the vendor. Many of Google's GTM tags are set up by default to still load in the browser while sending a user's consent preference to the platform. If desired, you can block the tag entirely by specifying 'Additional Consents Required' in the GTM tag's settings.`;
              }
              return "This script still loaded when an opt-out or GPC signal was active.";
            }
            if (
              (typedItem.respectingOptOut == null || isPending) &&
              typedItem.consentCategories.some((c) => isOptOutCheckableCategory(c))
            ) {
              return "Consent categories have changed since the last scan. Run a new scan to check for opt-out behavior.";
            }
            return null;
          };

          const tooltipText = getTooltipForOptOutRespecting();
          const hasOptOutCategory = typedItem.consentCategories?.some((c) =>
            isOptOutCheckableCategory(c),
          );
          const hasCategoriesButNoOptOut =
            (typedItem.consentCategories?.length ?? 0) > 0 && !hasOptOutCategory;
          const isLDUStyleTracker = isGoogleTracker(typedItem);
          const noOptOutTextClass = isLDUStyleTracker ? "text-gray-500" : "text-red-500";

          const inside = (
            <div className="flex gap-1 justify-center align-items-center">
              {managedByVendor ? (
                <div className="flex flex-col items-center">
                  <small>Managed by</small>
                  <strong className="flex items-center">
                    <VendorLogo
                      vendorLogoUrl={managedByVendor.logoUrl}
                      vendorName={managedByVendor.name}
                      size="xs"
                    />
                    <span className="ml-xxs">{managedByVendor.name}</span>
                  </strong>
                </div>
              ) : (typedItem.consentCategories?.length ?? 0) ===
                0 ? null : hasCategoriesButNoOptOut ? (
                <small className="text-gray-400">N/A</small>
              ) : isPending || typedItem.respectingOptOut == null ? (
                <small className="text-gray-400">Scan again</small>
              ) : typedItem.respectingOptOut ? (
                <>
                  <CheckIcon className="w-[20px] h-[20px] text-green-600" />
                  <span className="text-green-600">Yes</span>
                </>
              ) : (
                <>
                  <NoSymbolIcon className={clsx("w-[20px] h-[20px]", noOptOutTextClass)} />
                  <span className={clsx(noOptOutTextClass)}>No</span>
                </>
              )}
              {tooltipText && !managedByVendor && (
                <InformationCircleIcon className="w-[20px] h-[20px] text-gray-500" />
              )}
            </div>
          );

          if (tooltipText) {
            return (
              <ToolTip text={tooltipText} maxWidth="400px" minWidth="200px">
                {inside}
              </ToolTip>
            );
          }
          return inside;
        }
      },
    });
  }

  return (
    <div className={clsx("tw-boundary", className)}>
      <p className="mb-md">
        We've scanned your website with TrueScan. Review any uncategorized scripts and indicate
        their required consents. Using this list, set up the same consent requirements in your tag
        delivery platform (e.g., GTM).
      </p>
      {trackersDetected?.length > 0 && (
        <Table<TableRowData>
          columns={columnsForCategorizedTrackers}
          data={groupTrackersByService(trackersDetected)}
          keyExtractor={(item) => item.id}
          heading={
            <div className="flex items-center gap-2">
              <span>Detected Scripts</span>
              {lastScan && (
                <Pill variant="default">
                  <small style={{ fontWeight: "normal" }} className="mr-1">
                    Last scan:
                  </small>
                  <DateDisplay
                    date={lastScan}
                    variant={DateDisplayVariant.FROM_NOW}
                    hoverForRelative
                    inline
                  />
                </Pill>
              )}
              {onDownloadCsv && (
                <Button color="tertiary" size="s" onClick={onDownloadCsv}>
                  <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
                  Download Results as a CSV
                </Button>
              )}
            </div>
          }
          description={
            "These scripts and pixels are currently detected on your website." +
            (consentScannedFromLocationName
              ? ` Consent is checked by scanning from ${consentScannedFromLocationName} and performing a Decline All.`
              : "")
          }
          tableStyle={TableStyle.OUTLINED}
          disabledRowIds={pendingRowIds}
          className="mt-md"
        />
      )}
      <div style={{ display: "none" }}>
        <Table
          columns={[
            {
              key: "serviceName",
              header: "Service",
              render: (item) => <TrackerRowServiceName item={item} />,
            },
            {
              key: "url",
              header: "URLs",
              render: (item) => (
                <>
                  <CopyableText
                    className="text-sm"
                    style={{ left: "-0.7rem" }}
                    maxWidth={"400px"}
                    shortText={buildUrlShortText(item.url)}
                    longText={item.url}
                  />
                  {/* TODO: When we combine the rows, we should display the combined URLs more nicely -- item.urls.length > 1 && (
                  <>
                    <a href="#" className="url-more">
                      {item.urls.length - 1} more
                    </a>
                  </>
                ) */}
                </>
              ),
            },
            {
              key: "lastSeenAt",
              header: "Last Seen",
              render: (item) => <DateDisplay date={item.lastSeenAt} hoverForRelative />,
            },
          ]}
          data={trackersGoneMissing?.sort(sortTrackersByServiceName)}
          actions={[
            {
              label: "Removed On Purpose",
              onClick: (item: any) =>
                handleAcknowledgeTracker(item.id, "REMOVED" as TrueScanTrackerAcknowledgement),
              icon: <TrashIcon className="w-[20px] h-[20px] text-gray-500" />,
            },
          ]}
          actionStyle={ActionStyle.ICONS}
          keyExtractor={(item) => item.id}
          heading="Previously Seen Scripts"
          description="These scripts were previously detected but have gone missing (when accessed with accept-all consent)."
          tableStyle={TableStyle.OUTLINED}
          disabledRowIds={pendingRowIds}
        />
      </div>

      {trackersIgnored?.length > 0 && (
        <Table
          columns={[
            {
              key: "serviceName",
              header: "Service",
              render: (item) => (
                <div className="flex" style={{ lineHeight: "18px" }}>
                  <div className="w-1/2 pr-2">
                    <VendorLogo
                      vendorLogoUrl={item.serviceIconUrl}
                      vendorName={item.serviceName}
                      size="s"
                    />
                  </div>
                  <div className="w-1/2">
                    <span
                      className={clsx(!isUnknownService(item) && "font-semibold text-gray-900")}
                    >
                      {item.serviceName}
                    </span>
                    <br />
                    <small className="font-medium text-gray-500 text-transform-capitalize">
                      {item.type?.toLowerCase()}
                    </small>
                  </div>
                </div>
              ),
            },
            {
              key: "url",
              header: "URLs",
              render: (item) => (
                <>
                  <CopyableText
                    className="text-sm"
                    style={{ left: "-0.7rem" }}
                    maxWidth={"400px"}
                    shortText={buildUrlShortText(item.url)}
                    longText={item.url}
                  />

                  {/* TODO: When we combine the rows, we should display the combined URLs more nicely -- item.urls.length > 1 && (
                  <>
                    <a href="#" className="url-more">
                      {item.urls.length - 1} more
                    </a>
                  </>
                ) */}
                </>
              ),
            },
            {
              key: "firstSeenAtn",
              header: "First Seen",
              render: (item) => <DateDisplay date={item.firstSeenAt} hoverForRelative />,
            },
          ]}
          data={trackersIgnored?.sort(sortTrackersByServiceName)}
          actions={[
            {
              label: "Un-ignore",
              onClick: (item: any) => handleAcknowledgeTracker(item.id, "DETECTED"),
              icon: <ArrowUturnLeftIcon className="w-[20px] h-[20px] text-gray-500" />,
            },
          ]}
          actionStyle={ActionStyle.ICONS}
          keyExtractor={(item) => item.id}
          heading="Ignored Scripts"
          description="These scripts were previously detected and ignored. You may remove them from this list if they were ignored in error."
          tableStyle={TableStyle.OUTLINED}
          disabledRowIds={pendingRowIds}
          className="mt-md"
        />
      )}
      {vendorDialogOpenForTracker && (
        <Modal
          title={`Select the platform that manages ${vendorDialogOpenForTracker.serviceName}`}
          body={
            <>
              <span>
                When a script is managed by a platform (like Shopify App Pixels), you are indicating
                that the script is controlled by consent signals that the platform receives from our
                banner.
              </span>{" "}
              <a
                href="https://knowledge.truevault.com/article/346-testing-shopify-app-pixel-consent"
                rel="noreferrer"
                target="_blank"
                className="text-blue-600"
              >
                Learn more about Shopify App Pixel consent management
              </a>
              .
            </>
          }
          onClose={closeManagedByDialog}
          firstButton="Cancel"
          onFirstButtonClick={closeManagedByDialog}
          contentContainerClasses="pt-4"
          showOverlay={true}
          maxPanelWidth="max-w-[686px]"
        >
          <Autocomplete
            items={orgDataRecipients.map((vendor) => ({
              id: vendor.vendorId,
              name: vendor.name,
              logoUrl: vendor.logoUrl,
            }))}
            selectedItem={selectedManagingVendorDataRecipient}
            onSelect={async (item) => {
              await handleManagingVendorSelect(
                vendorDialogOpenForTracker,
                orgDataRecipients.find((v) => v.vendorId === item.id) || null,
              );
            }}
            leadingElement={
              selectedManagingVendorDataRecipient ? (
                <div className="w-[50px] h-full flex items-center justify-center">
                  <img
                    src={selectedManagingVendorDataRecipient.logoUrl}
                    alt={selectedManagingVendorDataRecipient.name}
                    className="h-[32px] w-[32px]"
                  />
                </div>
              ) : (
                <div className="w-[50px] h-full border border-solid border-neutral-400 rounded-l-sm flex items-center justify-center">
                  <MagnifyingGlassIcon className="w-[18px] h-[28px] text-neutral-900" />
                </div>
              )
            }
            // 66px = 50px (width of leading element) + 16px (padding on the left side of the input)
            leadingInputPaddingPx={66}
            trailingElement={
              selectedManagingVendorDataRecipient ? (
                <div className="p-3 flex items-center">
                  <Button
                    color="tertiary"
                    size="s"
                    onClick={async () => {
                      await handleManagingVendorSelect(vendorDialogOpenForTracker, null);
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ) : undefined
            }
          />
          {orgDataRecipientsForQuickSelectionAsManagers?.length > 0 && (
            <div className="flex mt-sm items-center gap-2">
              <h2 className="text-base font-semibold text-gray-900">Quick Select:</h2>
              <div className="flex flex-wrap gap-2">
                {orgDataRecipientsForQuickSelectionAsManagers.map((vendor) => (
                  <Button
                    key={vendor.vendorId}
                    color="tertiary"
                    onClick={async () => {
                      await handleManagingVendorSelect(vendorDialogOpenForTracker, vendor);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <VendorLogo
                        vendorLogoUrl={vendor.logoUrl}
                        vendorName={vendor.name}
                        size="xs"
                      />
                      <span>{vendor.name}</span>
                    </div>
                  </Button>
                ))}
              </div>
            </div>
          )}
        </Modal>
      )}
      {mixedConsentModalOpen && (
        <Modal
          title="Set Group Consent Categories"
          body={
            <>
              <p className="mb-4">
                The scripts in the <strong>{mixedConsentModalOpen.group.serviceName}</strong> group
                currently have different consent categories assigned. Proceeding will set all
                scripts in the group to have only the selected category, removing any other
                categories they may have had.
              </p>
            </>
          }
          onClose={() => setMixedConsentModalOpen(null)}
          firstButton="Cancel"
          onFirstButtonClick={() => setMixedConsentModalOpen(null)}
          secondButton="Proceed"
          onSecondButtonClick={async () => {
            if (
              mixedConsentModalOpen.action === "vendor" &&
              mixedConsentModalOpen.value?.openDialog
            ) {
              // Open vendor dialog for the group
              openManagedByDialog(
                mixedConsentModalOpen.group.trackers[0],
                mixedConsentModalOpen.group,
              );
            } else {
              await handleGroupAction(
                mixedConsentModalOpen.group,
                mixedConsentModalOpen.action as "categorize" | "acknowledge" | "vendor",
                mixedConsentModalOpen.value,
              );
            }
            setMixedConsentModalOpen(null);
          }}
          contentContainerClasses="pt-4"
          showOverlay={true}
          maxPanelWidth="max-w-[600px]"
        />
      )}
    </div>
  );
};
